import { CELL_INPUT_KINDS, FILE_COLUMN_TYPES, INPUT_ID_PATTERN, INPUT_KINDS, CellInput, InputSheet, SheetInput, SweepCondition, SweepInput, TableInputColumn, columnInput, findInput, isFileColumn, sheetInputs } from '../inputs';

/**
 * An input sheet is a **schema**: Fusaware-authored, versioned on its own,
 * and named by the report bundles filled in from it (and, one day, by the
 * procedures that fill it in). A bundle names its schema in `schema.json`;
 * the server writes `inputs.generated.ts` from that schema's sheet on every
 * save, so the report's code reads the inputs typed. The generated file is
 * never edited — it is replaced.
 *
 * In a bundle's working files — the studio's editor, a CLI directory —
 * `inputs.json` is the schema's draft sheet, there to be edited beside the
 * report. It is never stored with the bundle: saving the bundle saves it to
 * the schema's draft.
 */

export const INPUT_SHEET_FILE = 'inputs.json';
export const INPUT_SHEET_TYPES_FILE = 'inputs.generated.ts';
/** names the schema a report filled in from inputs reads: `{ "schema": "<key>" }`, or `{ "schema": "<key>", "version": 3 }` to pin a published version */
export const SCHEMA_REF_FILE = 'schema.json';

const isObject = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const COLUMN_TYPES: readonly string[] = ['string', 'number', 'integer', 'choice', ...FILE_COLUMN_TYPES];

/** The sheet in `inputs.json`, or what is wrong with it. Problems name where they are, e.g. `sections[1].inputs[0].id`. */
export function parseInputSheet(source: string): { sheet: InputSheet | null; problems: string[] } {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch (error) {
    return { sheet: null, problems: [`${INPUT_SHEET_FILE} is not JSON: ${error instanceof Error ? error.message : String(error)}`] };
  }
  const problems = checkInputSheet(raw);
  return { sheet: problems.length ? null : (raw as InputSheet), problems };
}

/** What is wrong with a sheet; nothing when it is one. */
export function checkInputSheet(raw: unknown): string[] {
  const problems: string[] = [];
  if (!isObject(raw) || !Array.isArray(raw.sections)) return ['an input sheet is { "sections": [{ "id", "title", "inputs": [...] }] }'];
  const sectionIds = new Set<string>();
  const inputIds = new Set<string>();
  raw.sections.forEach((section, s) => {
    const at = `sections[${s}]`;
    if (!isObject(section)) {
      problems.push(`${at} must be an object`);
      return;
    }
    if (typeof section.id !== 'string' || !INPUT_ID_PATTERN.test(section.id)) problems.push(`${at}.id must be lowercase letters, digits and underscores, starting with a letter`);
    else if (sectionIds.has(section.id)) problems.push(`${at}.id "${section.id}" is used twice`);
    else sectionIds.add(section.id);
    if (typeof section.title !== 'string' || !section.title.trim()) problems.push(`${at}.title must be a non-empty string`);
    if (!Array.isArray(section.inputs)) {
      problems.push(`${at}.inputs must be an array`);
      return;
    }
    section.inputs.forEach((input, i) => {
      const where = `${at}.inputs[${i}]`;
      problems.push(...checkInput(input, where, INPUT_KINDS));
      if (isObject(input) && typeof input.id === 'string') {
        if (inputIds.has(input.id)) problems.push(`${where}.id "${input.id}" is used twice; every input on the sheet needs its own`);
        inputIds.add(input.id);
      }
    });
  });
  if (problems.length) return problems;
  // sweeps last: a condition set by the report runner names another input
  const sheet = raw as unknown as InputSheet;
  sheet.sections.forEach((section, s) => section.inputs.forEach((input, i) => {
    if (input.kind === 'sweep') problems.push(...checkSweep(sheet, input, `sections[${s}].inputs[${i}]`));
  }));
  return problems;
}

function checkInput(input: unknown, at: string, kinds: readonly string[]): string[] {
  if (!isObject(input)) return [`${at} must be an object`];
  const problems: string[] = [];
  if (typeof input.id !== 'string' || !INPUT_ID_PATTERN.test(input.id)) problems.push(`${at}.id must be lowercase letters, digits and underscores, starting with a letter`);
  if (typeof input.kind !== 'string' || !kinds.includes(input.kind)) {
    problems.push(`${at}.kind must be one of ${kinds.join(', ')}`);
    return problems;
  }
  if (typeof input.label !== 'string' || !input.label.trim()) problems.push(`${at}.label must be a non-empty string`);
  for (const flag of ['required', 'carryOver', 'multiple', 'multiline', 'onlyDefaults']) {
    if (input[flag] !== undefined && typeof input[flag] !== 'boolean') problems.push(`${at}.${flag} must be true or false`);
  }
  for (const text of ['help', 'unit', 'placeholder', 'xLabel', 'yLabel']) {
    if (input[text] !== undefined && typeof input[text] !== 'string') problems.push(`${at}.${text} must be a string`);
  }
  switch (input.kind) {
    case 'choice':
      if (!Array.isArray(input.choices) || input.choices.length === 0 || !input.choices.every((c) => typeof c === 'string' && c.trim())) problems.push(`${at}.choices must be a list of one or more names`);
      else if (new Set(input.choices).size !== input.choices.length) problems.push(`${at}.choices names a choice twice`);
      break;
    case 'names':
      if (input.defaults !== undefined && !(Array.isArray(input.defaults) && input.defaults.every((x) => typeof x === 'string'))) problems.push(`${at}.defaults must be a list of names`);
      break;
    case 'numbers':
      if (input.defaults !== undefined && !(Array.isArray(input.defaults) && input.defaults.every((x) => typeof x === 'number' && Number.isFinite(x)))) problems.push(`${at}.defaults must be a list of numbers`);
      break;
    case 'file':
      if (input.accept !== undefined && !(Array.isArray(input.accept) && input.accept.every((x) => typeof x === 'string' && x.startsWith('.')))) problems.push(`${at}.accept must be a list of extensions such as ".csv"`);
      break;
    case 'table':
      problems.push(...checkColumns(input.columns, `${at}.columns`));
      if (input.rows !== undefined && !(Array.isArray(input.rows) && input.rows.every(isObject))) problems.push(`${at}.rows must be a list of rows`);
      break;
    case 'sweep':
      problems.push(...checkSweepShape(input, at));
      break;
  }
  return problems;
}

function checkColumns(columns: unknown, at: string): string[] {
  if (!Array.isArray(columns) || columns.length === 0) return [`${at} must be a list of one or more columns`];
  const problems: string[] = [];
  const ids = new Set<string>();
  columns.forEach((column, c) => {
    if (!isObject(column)) {
      problems.push(`${at}[${c}] must be an object`);
      return;
    }
    if (typeof column.id !== 'string' || !INPUT_ID_PATTERN.test(column.id)) problems.push(`${at}[${c}].id must be lowercase letters, digits and underscores, starting with a letter`);
    else if (ids.has(column.id)) problems.push(`${at}[${c}].id "${column.id}" is used twice`);
    else ids.add(column.id);
    if (typeof column.title !== 'string' || !column.title.trim()) problems.push(`${at}[${c}].title must be a non-empty string`);
    if (!COLUMN_TYPES.includes(column.type as string)) problems.push(`${at}[${c}].type must be one of ${COLUMN_TYPES.join(', ')}`);
    if (column.type === 'choice' && !(Array.isArray(column.choices) && column.choices.every((x) => typeof x === 'string'))) problems.push(`${at}[${c}].choices must be a list of names`);
    if (column.multiple !== undefined && typeof column.multiple !== 'boolean') problems.push(`${at}[${c}].multiple must be true or false`);
    for (const text of ['help', 'unit', 'width', 'xLabel', 'yLabel']) {
      if (column[text] !== undefined && typeof column[text] !== 'string') problems.push(`${at}[${c}].${text} must be a string`);
    }
    if (column.accept !== undefined && !(Array.isArray(column.accept) && column.accept.every((x) => typeof x === 'string' && x.startsWith('.')))) problems.push(`${at}[${c}].accept must be a list of extensions such as ".csv"`);
  });
  return problems;
}

function checkSweepShape(input: Record<string, unknown>, at: string): string[] {
  const problems: string[] = [];
  if (!Array.isArray(input.conditions) || input.conditions.length === 0) problems.push(`${at}.conditions must be a list of one or more conditions`);
  else {
    const ids = new Set<string>();
    input.conditions.forEach((condition, c) => {
      const where = `${at}.conditions[${c}]`;
      if (!isObject(condition)) {
        problems.push(`${where} must be an object`);
        return;
      }
      if (typeof condition.id !== 'string' || !INPUT_ID_PATTERN.test(condition.id)) problems.push(`${where}.id must be lowercase letters, digits and underscores, starting with a letter`);
      else if (ids.has(condition.id)) problems.push(`${where}.id "${condition.id}" is used twice`);
      else ids.add(condition.id);
      if (typeof condition.label !== 'string') problems.push(`${where}.label must be a string`);
      if (condition.kind !== undefined && condition.kind !== 'names' && condition.kind !== 'numbers') problems.push(`${where}.kind must be "names" or "numbers"`);
      const hasValues = condition.values !== undefined;
      const hasInput = condition.input !== undefined;
      if (hasValues === hasInput) problems.push(`${where} needs either "values" (fixed in the bundle) or "input" (set by the report runner), not both`);
      if (hasValues && !(Array.isArray(condition.values) && condition.values.every((x) => typeof x === 'string' || (typeof x === 'number' && Number.isFinite(x))))) problems.push(`${where}.values must be a list of names or numbers`);
      if (hasInput && typeof condition.input !== 'string') problems.push(`${where}.input must name a names or numbers input`);
    });
  }
  if (!Array.isArray(input.record) || input.record.length === 0) problems.push(`${at}.record must be a list of one or more inputs to record in each cell`);
  else {
    const ids = new Set<string>();
    input.record.forEach((cell, r) => {
      problems.push(...checkInput(cell, `${at}.record[${r}]`, CELL_INPUT_KINDS));
      if (isObject(cell) && typeof cell.id === 'string') {
        if (ids.has(cell.id)) problems.push(`${at}.record[${r}].id "${cell.id}" is used twice`);
        ids.add(cell.id);
      }
    });
  }
  const layout = input.layout;
  if (!isObject(layout) || !['tabs', 'rows', 'columns'].every((shelf) => Array.isArray(layout[shelf]) && (layout[shelf] as unknown[]).every((x) => typeof x === 'string'))) {
    problems.push(`${at}.layout must be { "tabs": [...], "rows": [...], "columns": [...] }, each a list of condition ids`);
  } else if (layout.view !== undefined && layout.view !== 'grid' && layout.view !== 'flat') problems.push(`${at}.layout.view must be "grid" or "flat"`);
  return problems;
}

function checkSweep(sheet: InputSheet, sweep: SweepInput, at: string): string[] {
  const problems: string[] = [];
  sweep.conditions.forEach((condition, c) => {
    if (condition.input === undefined) return;
    const source = findInput(sheet, condition.input);
    if (!source) problems.push(`${at}.conditions[${c}].input "${condition.input}" is not an input on the sheet`);
    else if (source.kind !== 'names' && source.kind !== 'numbers') problems.push(`${at}.conditions[${c}].input "${condition.input}" is a ${source.kind} input; it must be names or numbers`);
  });
  const placed = [...sweep.layout.tabs, ...sweep.layout.rows, ...sweep.layout.columns];
  for (const condition of sweep.conditions) {
    const times = placed.filter((id) => id === condition.id).length;
    if (times === 0) problems.push(`${at}.layout puts condition "${condition.id}" on no shelf; put it on tabs, rows or columns`);
    if (times > 1) problems.push(`${at}.layout puts condition "${condition.id}" on more than one shelf`);
  }
  for (const id of placed) {
    if (!sweep.conditions.some((c) => c.id === id)) problems.push(`${at}.layout names "${id}", which is not one of its conditions`);
  }
  return problems;
}

// ── the generated types ─────────────────────────────────────────────────────

const quote = (text: string) => JSON.stringify(text);
const comment = (text: string | undefined) => (text ? ` /** ${text.replace(/\*\//g, '*\\/').replace(/\s+/g, ' ')} */` : '');

function columnType(column: TableInputColumn, sheet: InputSheet): string {
  if (isFileColumn(column)) return valueType(columnInput(column), sheet);
  if (column.type === 'number' || column.type === 'integer') return 'number | null';
  if (column.type === 'choice' && column.choices?.length) return `${column.choices.map(quote).join(' | ')} | null`;
  return 'string | null';
}

function valueType(input: SheetInput | CellInput, sheet: InputSheet): string {
  switch (input.kind) {
    case 'number':
      return 'number | null';
    case 'text':
    case 'date':
      return 'string';
    case 'choice':
      return `${input.choices.map(quote).join(' | ')} | ''`;
    case 'yesno':
      return 'boolean | null';
    case 'names':
      return 'string[]';
    case 'numbers':
      return 'number[]';
    case 'image':
    case 'file':
      return 'FileRef[]';
    case 'waveform':
      return 'FileRef | null';
    case 'table':
      return `Array<{ ${input.columns.map((c) => `${c.id}: ${columnType(c, sheet)}`).join('; ')} }>`;
    case 'sweep': {
      const at = input.conditions.map((c) => {
        const source = c.input ? findInput(sheet, c.input) : undefined;
        const numeric = source ? source.kind === 'numbers' : (c.kind ?? (c.values?.every((x) => typeof x === 'number') ? 'numbers' : 'names')) === 'numbers';
        return `${c.id}: ${numeric ? 'number' : 'string'}`;
      });
      const record = input.record.map((r) => `${r.id}: ${valueType(r, sheet)}`);
      return `SweepValue<{ ${at.join('; ')} }, { ${record.join('; ')} }>`;
    }
  }
}

/** `inputs.generated.ts` for a sheet: the `Inputs` interface and the sheet typed by it. */
export function inputSheetTypesSource(sheet: InputSheet): string {
  const lines = sheet.sections.flatMap((section) => [
    `  // ${section.title}`,
    ...section.inputs.map((input) => `  ${input.id}: ${valueType(input, sheet)};${comment(input.label + (input.kind === 'number' && input.unit ? ` (${input.unit})` : ''))}`),
  ]);
  // the sheet is written out here rather than imported, so the studio's editor (which reads no JSON modules) type-checks it too
  return `// Generated from ${INPUT_SHEET_FILE} each time the bundle is saved. Change the sheet (the studio's Inputs tab, or ${INPUT_SHEET_FILE}), not this file.
import { FileRef, SweepValue, typedSheet } from '@veriform/report-sdk';

/** What the input sheet collects, by input id. */
export interface Inputs {
${lines.join('\n')}
}

export const sheet = typedSheet<Inputs>(${JSON.stringify(sheet, null, 2)});

// keep the imports used when the sheet has no files or sweeps
export type { FileRef, SweepValue };
`;
}

/**
 * The files with `inputs.generated.ts` brought up to the schema the bundle
 * names: written from its sheet, left as it was while the sheet has problems
 * (`null`; the compile reports them), and removed from a bundle that names no
 * schema.
 */
export function withInputSheetTypes<F extends Record<string, string>>(files: F, sheet: InputSheet | null): F {
  if (!(SCHEMA_REF_FILE in files)) {
    if (!(INPUT_SHEET_TYPES_FILE in files)) return files;
    const { [INPUT_SHEET_TYPES_FILE]: _removed, ...rest } = files;
    return rest as F;
  }
  return sheet ? { ...files, [INPUT_SHEET_TYPES_FILE]: inputSheetTypesSource(sheet) } : files;
}

// ── the schema a bundle names ───────────────────────────────────────────────

/** `schema.json`: the schema's key, and the published version when the bundle pins one rather than following the draft. */
export interface SchemaRef {
  schema: string;
  version?: number;
}

/** The reference in `schema.json`, or what is wrong with it. */
export function parseSchemaRef(source: string): { ref: SchemaRef | null; problems: string[] } {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch (error) {
    return { ref: null, problems: [`${SCHEMA_REF_FILE} is not JSON: ${error instanceof Error ? error.message : String(error)}`] };
  }
  const usage = `${SCHEMA_REF_FILE} is { "schema": "<key>" } to follow the schema's draft, or { "schema": "<key>", "version": <n> } to pin a published version`;
  if (!isObject(raw) || typeof raw.schema !== 'string' || !raw.schema.trim()) return { ref: null, problems: [usage] };
  if (raw.version !== undefined && !(typeof raw.version === 'number' && Number.isInteger(raw.version) && raw.version > 0)) return { ref: null, problems: [`${SCHEMA_REF_FILE}: version must be a published version number, e.g. 3`] };
  const extra = Object.keys(raw).filter((k) => k !== 'schema' && k !== 'version');
  if (extra.length) return { ref: null, problems: [`${usage}; it has ${extra.map((k) => `"${k}"`).join(', ')} as well`] };
  return { ref: raw.version === undefined ? { schema: raw.schema } : { schema: raw.schema, version: raw.version as number }, problems: [] };
}

export function schemaRefSource(ref: SchemaRef): string {
  return `${JSON.stringify(ref, null, 2)}\n`;
}

/** JSON with every object's keys in order, so two sheets compare equal however they were stored (jsonb reorders keys). */
export function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isObject(value)) return `{${Object.keys(value).sort().filter((k) => value[k] !== undefined).map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
  return JSON.stringify(value);
}

/** The same sheet, to the last label: whether a draft is a version already. */
export function sameSheet(a: InputSheet, b: InputSheet): boolean {
  return canonical(a) === canonical(b);
}

// ── what changed since the last version ─────────────────────────────────────

/**
 * Something a published version had that this sheet no longer means the same
 * way: an id gone, renamed (another id with the same label took its place),
 * or asking for another kind of value; a number in another unit; choices taken
 * away, or a list held to its defaults (`onlyDefaults`) where it was not; one
 * file where there were several; a sweep over a new condition, or a
 * condition taking its values from somewhere else, whose cells no longer line
 * up. `path` is how the report's code reaches it: `serial_number`,
 * `input_current.current` (a sweep's condition or recorded input),
 * `equipment.model` (a table's column).
 *
 * `choicesAdded`, `multiple` with `now: 'several'` and `onlyDefaults` with
 * `now: 'off'` break only the other way: code on the earlier version misreading a record on the later one
 * (`olderReaderChanges`).
 */
export interface SheetIdChange {
  path: string;
  label: string;
  change: 'removed' | 'renamed' | 'kind' | 'unit' | 'choices' | 'choicesAdded' | 'multiple' | 'onlyDefaults' | 'condition' | 'source';
  /** renamed: the new path */
  to?: string;
  /**
   * kind: what it was, and is; unit: the units (empty for none); choices: the
   * choices (or a sweep condition's fixed values, or an `onlyDefaults` list's
   * defaults) taken away; choicesAdded: the ones added; multiple: `one` or
   * `several` files now; onlyDefaults: `on` or `off` now; source: the input a
   * sweep condition took its values from, and takes them from now (empty when
   * fixed on the sheet)
   */
  from?: string;
  now?: string;
}

interface Named { id: string; label: string; kind?: string; unit?: string; choices?: string[]; onlyDefaults?: boolean; multiple?: boolean; source?: string }

/** What changed in one named thing that is still there, if anything breaks. */
function changeOf(path: string, old: Named, now: Named): SheetIdChange | null {
  if (old.kind && now.kind && old.kind !== now.kind) return { path, label: now.label, change: 'kind', from: old.kind, now: now.kind };
  if ((old.unit ?? '') !== (now.unit ?? '')) return { path, label: now.label, change: 'unit', from: old.unit ?? '', now: now.unit ?? '' };
  if ((old.source ?? '') !== (now.source ?? '')) return { path, label: now.label, change: 'source', from: old.source ?? '', now: now.source ?? '' };
  if (!old.onlyDefaults && now.onlyDefaults) return { path, label: now.label, change: 'onlyDefaults', now: 'on' };
  const gone = old.choices && now.choices ? old.choices.filter((c) => !now.choices?.includes(c)) : [];
  if (gone.length) return { path, label: now.label, change: 'choices', from: gone.join(', ') };
  if (old.multiple && now.multiple === false) return { path, label: now.label, change: 'multiple', now: 'one' };
  return null;
}

/** Compare one list of named things, e.g. a sweep's conditions, before and after: what changed, and the paths only added. */
function compareIds(prefix: string, before: Named[], after: Named[]): { changed: SheetIdChange[]; added: string[] } {
  const path = (id: string) => (prefix ? `${prefix}.${id}` : id);
  const beforeIds = new Set(before.map((x) => x.id));
  const fresh = after.filter((x) => !beforeIds.has(x.id));
  const changes: SheetIdChange[] = [];
  for (const old of before) {
    const now = after.find((x) => x.id === old.id);
    if (now) {
      const change = changeOf(path(old.id), old, now);
      if (change) changes.push(change);
      continue;
    }
    // the same label under a new id, not already claimed: most likely renamed
    const twin = fresh.find((x) => x.label.trim().toLowerCase() === old.label.trim().toLowerCase());
    if (twin) {
      fresh.splice(fresh.indexOf(twin), 1);
      changes.push({ path: path(old.id), label: old.label, change: 'renamed', to: path(twin.id) });
    } else {
      changes.push({ path: path(old.id), label: old.label, change: 'removed' });
    }
  }
  return { changed: changes, added: fresh.map((x) => path(x.id)) };
}

/** What code on `before` misreads in a value entered on `after`, for each thing still there as the same kind: a choice it does not know, files past the first. */
function compareForOlder(prefix: string, before: Named[], after: Named[]): SheetIdChange[] {
  return before.flatMap((old): SheetIdChange[] => {
    const now = after.find((x) => x.id === old.id);
    if (!now || old.kind !== now.kind) return [];
    const path = prefix ? `${prefix}.${old.id}` : old.id;
    const added = old.choices?.length && now.choices ? now.choices.filter((c) => !old.choices?.includes(c)) : [];
    if (added.length) return [{ path, label: now.label, change: 'choicesAdded', from: added.join(', ') }];
    if (old.multiple === false && now.multiple) return [{ path, label: now.label, change: 'multiple', now: 'several' }];
    if (old.onlyDefaults && !now.onlyDefaults) return [{ path, label: now.label, change: 'onlyDefaults', now: 'off' }];
    return [];
  });
}

/** What a sweep condition's values are: names, or numbers, whether fixed in the sheet or set by the report runner. */
function conditionKind(sheet: InputSheet, condition: SweepCondition): 'names' | 'numbers' {
  const source = condition.input ? findInput(sheet, condition.input) : undefined;
  if (source) return source.kind === 'numbers' ? 'numbers' : 'names';
  return condition.kind ?? (condition.values?.every((x) => typeof x === 'number') ? 'numbers' : 'names');
}

function named(input: SheetInput | CellInput): Named {
  return {
    id: input.id,
    label: input.label,
    kind: input.kind,
    ...(input.kind === 'number' || input.kind === 'numbers' ? { unit: input.unit } : {}),
    ...(input.kind === 'choice' ? { choices: input.choices } : {}),
    // held to its defaults, a list's defaults are its choices: a value entered outside them is dropped
    ...((input.kind === 'names' || input.kind === 'numbers') && input.onlyDefaults ? { onlyDefaults: true, choices: (input.defaults ?? []).map(String) } : {}),
    ...(input.kind === 'image' || input.kind === 'file' ? { multiple: !!input.multiple } : {}),
  };
}

function column(c: TableInputColumn): Named {
  return { id: c.id, label: c.title, kind: c.type, ...(isFileColumn(c) ? {} : { unit: c.unit }), ...(c.type === 'choice' ? { choices: c.choices ?? [] } : {}) };
}

/** A condition fixed on the sheet compares its values as choices (cells recorded at one taken away are lost); one set by the report runner, the input it is set at. */
const condition = (sheet: InputSheet) => (c: SweepCondition): Named => {
  const source = c.input ? findInput(sheet, c.input) : undefined;
  return {
    id: c.id,
    label: c.label,
    kind: conditionKind(sheet, c),
    unit: c.unit ?? (source?.kind === 'numbers' ? source.unit : undefined),
    source: c.input ?? '',
    ...(c.input === undefined ? { choices: (c.values ?? []).map(String) } : {}),
  };
};

/**
 * What a new version's sheet changes against the one before it: `changed`,
 * what breaks anything reading the inputs by id — the report's code, a report
 * on the new version reading a record made on the old one, a run mapped onto
 * the sheet; and `added`, the paths that are new, which break none of those.
 * Labels, help, sections, layout and defaults are the form, not the data, and
 * are not compared — except the defaults of a list held to them; neither is `required`, since every value may already be
 * empty. A report on the old version reading a record on the new one has more
 * to fear: `olderReaderChanges`.
 */
export function sheetChanges(before: InputSheet, after: InputSheet): { changed: SheetIdChange[]; added: string[] } {
  const result = compareIds('', sheetInputs(before).map(named), sheetInputs(after).map(named));
  const add = (more: { changed: SheetIdChange[]; added: string[] }) => { result.changed.push(...more.changed); result.added.push(...more.added); };
  for (const old of sheetInputs(before)) {
    const now = findInput(after, old.id);
    if (!now || now.kind !== old.kind) continue;
    if (old.kind === 'sweep' && now.kind === 'sweep') {
      const conditions = compareIds(old.id, old.conditions.map(condition(before)), now.conditions.map(condition(after)));
      // a new condition is not an addition: every cell already recorded is now short of a coordinate
      result.changed.push(...conditions.changed, ...conditions.added.map((path) => ({ path, label: now.conditions.find((c) => `${now.id}.${c.id}` === path)?.label ?? '', change: 'condition' as const })));
      add(compareIds(old.id, old.record.map(named), now.record.map(named)));
    } else if (old.kind === 'table' && now.kind === 'table') {
      add(compareIds(old.id, old.columns.map(column), now.columns.map(column)));
    }
  }
  return result;
}

/** What a new version would break against the one before it (`sheetChanges`, without what is only added). */
export function sheetIdChanges(before: InputSheet, after: InputSheet): SheetIdChange[] {
  return sheetChanges(before, after).changed;
}

/**
 * What code on an earlier version misreads in a record entered on a later
 * one, beyond `sheetIdChanges`: a choice it does not offer (a choice input
 * reads it as empty, so a required one is missing; a sweep cell recording only
 * that is dropped; a table's choice column holds a value its type does not
 * have), and several files where it keeps the first. Values only fixed in a
 * sweep's condition are not among them: the cells recorded at a new one reach
 * the older code like any other.
 */
export function olderReaderChanges(older: InputSheet, newer: InputSheet): SheetIdChange[] {
  const changes = compareForOlder('', sheetInputs(older).map(named), sheetInputs(newer).map(named));
  for (const old of sheetInputs(older)) {
    const now = findInput(newer, old.id);
    if (old.kind === 'sweep' && now?.kind === 'sweep') changes.push(...compareForOlder(old.id, old.record.map(named), now.record.map(named)));
    else if (old.kind === 'table' && now?.kind === 'table') changes.push(...compareForOlder(old.id, old.columns.map(column), now.columns.map(column)));
  }
  return changes;
}

/**
 * Whether code written against `reader` reads what was entered on `entered`,
 * two versions of one schema, `readerIsOlder` saying which came first. Code on
 * the later version finds what was added empty and needs only nothing to break
 * (`sheetIdChanges`); code on the earlier one never asks for what was added,
 * but misreads choices added and files made several (`olderReaderChanges`).
 */
export function sheetsCompatible(reader: InputSheet, entered: InputSheet, readerIsOlder: boolean): boolean {
  if (!readerIsOlder) return sheetIdChanges(entered, reader).length === 0;
  return sheetIdChanges(reader, entered).length === 0 && olderReaderChanges(reader, entered).length === 0;
}
